import React, {
  ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Button,
  Dialog,
  FormContainer,
  FormItem,
  Row,
  Text,
  TextArea,
} from 'elmo-elements';

import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useFinaliseReviewerMutation,
  useGetCurrentTmsReviewerQuery,
  useGetReviewerElearningCommentsQuery,
  useUpdateCommentMutation,
} from '@/modules/services';
import { conditionalOutputHelper } from '@/lib/util';
import { ReviewerComment } from '@/modules/services/comments';
import { useParams } from 'react-router-dom';
import { ReviewParamType } from '../routes';
import { appData } from '@/lib/data';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';

interface ReviewCommentType {
  comment: string;
}

const schema = yup.object().shape({
  comment: yup.string().max(500, 'Maximum of 500 characters only'),
});

const ReviewComment = React.forwardRef((_, ref): ReactElement => {
  const dispatch = useDispatch();
  const { elearningId, frameId } = useParams<ReviewParamType>();
  const ckeditor = useRef<any>(null);
  const [isFinalised, setIsFinalised] = useState<boolean>(false);
  const [savedComments, setSavedComments] = useState<{
    [key: string]: string;
  }>({});
  const { data: reviewerData, isLoading: reviewerDataIsLoading } =
    useGetCurrentTmsReviewerQuery(elearningId);
  const {
    data: reviewerDataComments,
    isLoading: reviewerDataCommentsIsLoading,
    isSuccess: reviewerDataIsSuccess,
    refetch,
  } = useGetReviewerElearningCommentsQuery(elearningId);
  const [createComment, createCommentStatus] = useCreateCommentMutation();
  const [updateComment, updateCommentStatus] = useUpdateCommentMutation();
  const [deleteComment, deleteCommentStatus] = useDeleteCommentMutation();
  const [finaliseReviewer, finaliseReviewStatus] =
    useFinaliseReviewerMutation();

  const getFrameReviewerComments = (
    comments: ReviewerComment[],
    id: string,
  ): ReviewerComment[] => {
    if (comments.length <= 0) return [];

    const commentsByReviewer = comments.filter(
      (comment) => comment.frame.id === id,
    );

    if (commentsByReviewer.length <= 0) return [];

    return commentsByReviewer;
  };

  const getReviewerComment = (
    comments: ReviewerComment[],
    id: string,
  ): string => {
    if (savedComments[frameId]) return savedComments[frameId];
    const result = getFrameReviewerComments(comments, id);
    if (result.length <= 0) return '';
    return result[0].comment;
  };

  const isCurrentCommentsSaved = () => {
    const dataComment = getFrameReviewerComments(
      reviewerDataComments || [],
      frameId,
    );
    const commentValue = dataComment[0]?.comment || '';

    return commentValue === savedComments[frameId];
  };

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    getValues,
    register,
  } = useForm<ReviewCommentType>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!reviewerDataComments) return;

    const reviewerComment = getReviewerComment(reviewerDataComments, frameId);
    if (!savedComments[frameId] && reviewerComment)
      setValue('comment', reviewerComment);
  }, [frameId, reviewerDataComments]);

  useEffect(() => {
    if (reviewerData && !reviewerDataIsLoading) {
      setIsFinalised(+reviewerData.status === appData.commentStatus.completed);
    }
  }, [reviewerData, reviewerDataIsLoading]);

  /* Handles onChange - saves to state */
  /* Work around to avoid re-focus when using onChange/setValue handlers to save state */
  useEffect(() => {
    const sub = watch((value, { name }) => {
      setSavedComments((prev) => ({
        ...prev,
        [frameId]: value.comment || '',
      }));
    });

    return () => sub.unsubscribe();
  }, [watch, frameId]);

  /* Sets form initial value w/o touching */
  /* Re-Populates CKeditor when changing slides */
  useEffect(() => {
    if (reviewerDataIsLoading) return;

    const dataComment = getFrameReviewerComments(
      reviewerDataComments || [],
      frameId,
    );

    const commentValue = dataComment.length > 0 ? dataComment[0].comment : '';
    const savedComment = savedComments[frameId] || commentValue;

    setValue('comment', savedComment);
    if (!ckeditor.current) return;
    ckeditor.current.setData(getValues('comment'));
  }, [
    reviewerDataComments,
    reviewerDataCommentsIsLoading,
    frameId,
    setValue,
    getValues,
  ]);

  const handleSave = async (data: {
    comment: string;
  }): Promise<{ error?: string }> => {
    let errorMessage;
    if (isFinalised) return { error: undefined };

    const dataComment = getFrameReviewerComments(
      reviewerDataComments || [],
      frameId,
    );

    const commentId = dataComment.length > 0 ? dataComment[0].id : '';

    const submitData = {
      frameId,
      reviewerId: reviewerData ? reviewerData.id : '',
      id: commentId,
      content: {
        comment: data.comment,
      },
    };

    let message = 'Comments saved successfully.';
    try {
      if (commentId) {
        if (data.comment) {
          await updateComment(submitData).unwrap();
        } else {
          await deleteComment(commentId).unwrap();
        }
      } else if (data.comment) {
        await createComment(submitData).unwrap();
      }
    } catch (error) {
      message = 'Something went wrong.';
      errorMessage = message;
      console.error('Failed to submit data:', error);
    }
    await refetch();
    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
    return {
      error: errorMessage,
    };
  };

  const onCompleteReview = async () => {
    try {
      await finaliseReviewer(elearningId).unwrap();
      // If successful, reload the page to show the review completed message page
      window.location.reload();
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong.',
        },
      });
      console.error('Failed to submit data:', error);
    }
  };

  useImperativeHandle(ref, () => ({
    isCommentSaved: () => isCurrentCommentsSaved(),
    saveCurrentSlideComment: async () => {
      let error;
      await handleSubmit(async (data) => {
        const rs = await handleSave(data);
        error = rs?.error;
      })();
      return error;
    },
  }));

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <FormContainer className="review-comment-form" maxColumns={1}>
        <FormItem
          message={errors.comment?.message}
          status={conditionalOutputHelper(!!errors.comment, 'error', 'default')}
          isBlock={true}
        >
          {reviewerDataIsSuccess && (
            <TextArea
              className="review-comment-textarea"
              {...register('comment', {})}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('comment', e.target.value)
              }
              value={watch('comment')}
              ariaLabel="comment"
              id="Review-comment"
            />
          )}
        </FormItem>
      </FormContainer>
      {conditionalOutputHelper(
        isFinalised,
        <Text className="text-color-success">Finalised</Text>,

        <Row
          justifyContent="end"
          wrap="nowrap"
          className="review-btn-container"
        >
          <Button
            isLoading={finaliseReviewStatus.isLoading}
            type="default"
            isText={true}
            onClick={() => {
              Dialog.confirm({
                title: 'Complete Course Review',
                content:
                  'Are you sure you would like to complete your review? By clicking continue, you agree that you have reviewed the entire course and no longer need to add comments.',
                async onOk() {
                  if (!isCurrentCommentsSaved()) {
                    await handleSubmit(handleSave)();
                  }
                  await onCompleteReview();
                },
                okLabel: 'Continue',
                cancelLabel: 'Add more comments',
              });
            }}
            isUppercase={false}
          >
            Mark as complete
          </Button>
          <Button
            isLoading={
              createCommentStatus.isLoading ||
              updateCommentStatus.isLoading ||
              deleteCommentStatus.isLoading
            }
            type="primary"
            htmlType="submit"
            isUppercase={false}
          >
            Save
          </Button>
        </Row>,
      )}
    </form>
  );
});

export default ReviewComment;
